import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Link,
    List,
    ListItem,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import moment from 'moment';
import React, {useCallback, useEffect, useState} from 'react';
import CRM from '../../../APIs/CRM';
import {formatCurrency} from '../../../Core/helpers/formatCurrency';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import GreenCheck from '../../../Icons/GreenCheck';
import {ContactFormStore} from '../../ContactForm/ContactFormStore/ContactFormStore';
import {CalcfocusConsentButton} from '../utils/CalcfocusConsentButton';
import {useCommonModalStyles} from './common/modalStyles';

interface StartApplicationProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    formType: 'single-quote' | 'combination-quote' | undefined;
    quoteResponse: any[];
    onExit: () => void;
    onNext: () => void;
}
const useStyles = makeStyles(() => ({
    wrapper: {
        padding: '20px',
    },
    title: {
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '44px',
        lineHeight: '48.4px',
        color: '#3f51b5',
        marginBottom: '28px',
    },
    label: {
        marginTop: '18px',
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '30.27px',
        color: '#838383',
    },
    quoteHeader: {
        marginTop: '18px',
        fontFamily: 'LoraRegular',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    quoteSubTitle: {
        marginTop: '24px',
        marginBottom: '10px',
        fontFamily: 'FredokaRegular',
        fontWeight: 500,
        fontSize: '17.29px',
        lineHeight: '20.92px',
        color: '#434343',
        textTransform: 'uppercase',
    },
    quoteValue: {
        marginBottom: '40px',
        fontFamily: 'LoraRegular',
        fontWeight: 500,
        fontSize: '30px',
        lineHeight: '38.4px',
        color: '#3f51b5',
    },
    disclosureText: {
        marginTop: '32px',
        marginBottom: '36px',
        fontFamily: 'FredokaRegular',
        fontWeight: 500,
        fontSize: '18px',
        lineHeight: '28.8px',
        color: '#838383',
    },
    colorRed: {
        color: '#f65858',
    },
    text: {
        fontFamily: 'LoraSemiBold',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    navigation: {
        marginTop: '20px',
    },
    backBtn: {
        borderRadius: '20px',
    },
    disclosureWrapper: {
        backgroundColor: '#F4F6FF',
        borderRadius: '6px',
        border: '1px solid #3F51B5',
        padding: '15px 12px 24px 24px',
        margin: '20px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    disclosureTitle: {
        fontFamily: 'FredokaSemiBold',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '25.6px',
        color: '#3F51B5',
    },
    disclosureDescription: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '15px',
        lineHeight: '24px',
        color: '#3F51B5',
    },
    emailNote: {
        color: '#838383',
        fontFamily: 'FredokaRegular',
        margin: '0 auto',
        lineHeight: '25.6px',
        fontWeight: 400,
        letterSpasing: 0,
        opacity: 0.9,
        textAlign: 'center',
    },
    emailNoteMain: {
        fontFamily: 'FredokaBold',
        opacity: 0.9,
    },
    emailNoteHiglighted: {
        fontFamily: 'FredokaMedium',
    },
}));

const NOVEMBER_14_2024 = 1732406400000; // UTC midnight

export const StartApplication: React.FC<StartApplicationProps> = ({
    parameterStore,
    contactFormStore,
    quoteResponse,
    onNext,
    onExit,
}) => {
    const {approval} = parameterStore;

    const email = approval.emailAddress.value;
    const firstName = approval.firstName.value as string;
    const lastName = approval.lastName.value as string;
    const phoneNumber = approval.phoneNumber.value as string;
    const classes = useStyles();
    const commonClasses = useCommonModalStyles();

    const premium = quoteResponse?.find((qr) => qr.name === 'Initial monthly premium')?.value;
    const product = 'Prosper IUL';
    const cashValueAtAge = quoteResponse?.find((qr) => qr.fieldName === 'prosperValueAtAge');
    const deathBenefit = quoteResponse.find((qr) => qr.name === 'Initial death benefit')?.value;

    const [hasConsent, setHasConsent] = useState<null | boolean>(null);
    const [hasRequestedConsent, setHasRequestedConsent] = useState<null | boolean>(null);

    const fetchConsent = useCallback(async () => {
        const bday = approval.bday.value as number;
        const bmonth = approval.bmonth.value as number;
        const byear = approval.byear.value as number;
        let date = moment([byear, bmonth - 1, bday]);
        const dob = date.format('YYYY-MM-DD');

        const respConsent = await CRM.GetCustomerConsent(firstName, lastName, dob);

        // If no consent exists, set hasConsent to false and return early
        if (!respConsent?.consent || !('date' in respConsent.consent)) {
            setHasConsent(false);
            return;
        }

        const isValidConsent = respConsent.consent.date > NOVEMBER_14_2024;
        console.log(
            `Consent status: ${isValidConsent ? 'Valid' : 'Needs renewal'} - ` +
                `Date: ${new Date(respConsent.consent.date).toLocaleDateString()}`,
        );

        if (!isValidConsent) {
            console.log('Consent renewal required: Previous consent from before November 14, 2024');
        }

        setHasConsent(isValidConsent);
    }, [firstName, lastName]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout;

        if (hasRequestedConsent && !hasConsent) {
            intervalId = setInterval(fetchConsent, 1000);
        }

        return () => clearInterval(intervalId); // Cleanup on unmount or when polling stops
    }, [hasRequestedConsent, hasConsent]);

    useEffect(() => {
        fetchConsent();
    }, []);

    const quoteInfo = [
        ['Product', product],
        ['Death Benefit', formatCurrency(deathBenefit ?? 0)],
        [cashValueAtAge?.name, formatCurrency(cashValueAtAge?.value ?? 0)],
        ['Est. Montly Premium', formatCurrency(premium ?? 0)],
    ];

    const consentSection = useCallback(
        () =>
            hasConsent ? (
                <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                        <GreenCheck />
                    </Grid>
                    <Grid item>
                        <Typography classes={{root: classes.disclosureText}}>
                            Client has already provided consent.
                        </Typography>
                    </Grid>
                </Grid>
            ) : (
                <>
                    <Typography classes={{root: `${classes.disclosureText} ${classes.colorRed}`}}>
                        Client has not provided consent.
                    </Typography>
                    <List>
                        <ListItem>
                            <Link
                                href="https://getamplifylife.com/policy/hipaa-compliant-authorization-for-life-insurance"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                HIPAA Authorization
                            </Link>
                        </ListItem>
                        <ListItem>
                            <Link
                                href="https://getamplifylife.com/policy/electronic-record-and-signature-disclosure-and-consent"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Electronic Record And Signature Disclosure
                            </Link>
                        </ListItem>
                        <ListItem>
                            <Link
                                href="https://getamplifylife.com/policy/telephone-consumer-protection-act-and-state-law-equivalents"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Telephone Consumer Protection Act and State Law Equivalent
                            </Link>
                        </ListItem>
                        <ListItem>
                            <Link
                                href="https://getamplifylife.com/policy/voice-signature-authorization"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Voice Signature Authorization
                            </Link>
                        </ListItem>
                        <ListItem>
                            <Link
                                href="https://www.getamplifylife.com/terms-and-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Terms and Conditions
                            </Link>
                        </ListItem>
                        <ListItem>
                            <Link
                                href="https://www.getamplifylife.com/policy/fraud-statement"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Fraud Statement
                            </Link>
                        </ListItem>
                        <ListItem>
                            <Link
                                href="https://getamplifylife.com/policy/fcra-authorization"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                FCRA Authorization
                            </Link>
                        </ListItem>
                    </List>
                </>
            ),
        [hasConsent],
    );

    if (hasConsent == null) {
        return (
            <div>
                <CircularProgress />
            </div>
        );
    }

    const onNextClick = async () => {
        await contactFormStore.saveForm();
        onNext();
    };

    return (
        <Grid container classes={{root: classes.wrapper}}>
            <Typography classes={{root: classes.title}}>Let's Start The Application.</Typography>
            <Grid container spacing={6}>
                <Grid item xs={8}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography className={classes.label}>Client's First Name</Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                value={firstName}
                                aria-label="Client's First Name"
                                disabled
                            ></TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography className={classes.label}>Client's Last Name</Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                value={lastName}
                                aria-label="Client's Last Name"
                                disabled
                            ></TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography className={classes.label}>Email</Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                value={email}
                                aria-label="Email"
                                disabled
                            ></TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography className={classes.label}>Phone Number</Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                value={phoneNumber}
                                aria-label="Phone Number"
                                disabled
                            ></TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography classes={{root: commonClasses.ph}}>
                                Please ensure the client’s email and phone number are correct to avoid issues with
                                coverage. You will not be able to revert client details once the application has
                                started.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography classes={{root: classes.text}}>Disclosures</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {consentSection()}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={4}>
                    <Typography classes={{root: classes.quoteHeader}}>Quote</Typography>
                    {quoteInfo.map(([title, value]) => (
                        <>
                            <Typography classes={{root: classes.quoteSubTitle}}>{title}</Typography>
                            <Typography classes={{root: classes.quoteValue}}>{value}</Typography>
                        </>
                    ))}
                </Grid>
            </Grid>
            <Box className={classes.disclosureWrapper}>
                <Typography classes={{root: classes.disclosureTitle}}>
                    Please Read This Statement To Your Client ~
                </Typography>
                {!hasConsent && (
                    <Typography classes={{root: classes.disclosureDescription}} style={{marginBottom: '8px'}}>
                        By providing or confirming your phone number, you agree to receive an automated text from
                        Amplify Life Insurance containing important consents to review and sign to proceed with your
                        application. If you do not agree to a text, you can elect another method to complete your
                        consents. Can we proceed with the text?
                    </Typography>
                )}
                <Typography classes={{root: classes.disclosureDescription}}>
                    The information you provide in this application is critical to our consideration of your request for
                    insurance coverage. You are strongly urged to answer all questions completely and accurately so that
                    we may provide you with the best coverage we can. We will seek information from other sources to
                    assist us with evaluating this application, potentially including your health care provider. If your
                    answers are incorrect, incomplete or untrue, it will delay your application, and The Company may
                    have the right to deny benefits or terminate coverage. Please know that your personal information,
                    including health information, is protected by The Company and only used by The Company to do
                    business with you, and as permitted or required by law.
                </Typography>
            </Box>
            {!hasConsent && (
                <Typography classes={{root: classes.emailNote}}>
                    <Typography component="span" classes={{root: classes.emailNoteMain}}>
                        Email not found?
                    </Typography>{' '}
                    Ask clients to check{' '}
                    <Typography component="span" classes={{root: classes.emailNoteHiglighted}}>
                        Spam
                    </Typography>
                    ,{' '}
                    <Typography component="span" classes={{root: classes.emailNoteHiglighted}}>
                        Promotions
                    </Typography>
                    ,{' '}
                    <Typography component="span" classes={{root: classes.emailNoteHiglighted}}>
                        Socials
                    </Typography>
                    , or{' '}
                    <Typography component="span" classes={{root: classes.emailNoteHiglighted}}>
                        Updates
                    </Typography>{' '}
                    tabs.
                </Typography>
            )}
            <Grid container spacing={2} justify="space-around" classes={{root: classes.navigation}}>
                <Grid item>
                    <Button
                        color="primary"
                        variant="text"
                        onClick={onExit}
                        startIcon={<KeyboardBackspaceIcon />}
                        classes={{root: classes.backBtn}}
                    >
                        Save and exit
                    </Button>
                </Grid>
                <Grid item>
                    {hasConsent ? (
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={onNextClick}
                            classes={{root: commonClasses.nextBtn}}
                        >
                            Next
                        </Button>
                    ) : (
                        <CalcfocusConsentButton
                            parameterStore={parameterStore}
                            onConsentSend={() => setHasRequestedConsent(true)}
                        />
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};
